import { useRouter } from 'expo-router';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { AuthChallengeEnum } from 'features/signup/challenges/AuthChallengeEnum/AuthChallengeEnum';
import { useCallback } from 'react';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { requestNotificationPermissions } from 'shared/utils/notifications/getNotificationStatus';

const useRedirect = () => {
  const router = useRouter();
  const { segmentClient } = useAnalyticsConfiguration();

  const challengeRedirect = useCallback(
    async (
      challenge: string,
      emailAddress: string,
      phoneNumber: string,
      lastFourDigitPhoneNumber: string,
      sessionID: string,
      screenType?: string,
      flowKey?: OtpFlowKey,
      challengeId?: string,
      identityId?: string,
    ) => {
      const notifyParams = {
        loginMethod: 'password',
        usernameType: phoneNumber ? 'phone' : 'email',
        deviceNotificationStatus: (await requestNotificationPermissions())
          .granted
          ? 'enabled'
          : 'disabled',
      };
      const anonymousId = await segmentClient.getAnonymousId();

      if (challenge === AuthChallengeEnum.verifyDevice.valueOf()) {
        router.replace({
          pathname:
            flowKey === OtpFlowKey.SIGNUP
              ? '/signup/verify-device'
              : '/login/verify-device',
          params: {
            anonymousId: anonymousId,
            emailAddress: emailAddress,
            phoneNumber: phoneNumber,
            session: sessionID,
            screenType: screenType,
            lastFourDigitPhoneNumber: lastFourDigitPhoneNumber,
            ...notifyParams,
          },
        });
      }
      if (challenge === AuthChallengeEnum.verifyPhoneNumber.valueOf()) {
        const navigate =
          flowKey === OtpFlowKey.SIGNUP ? router.push : router.replace;

        navigate({
          pathname:
            flowKey === OtpFlowKey.SIGNUP
              ? '/signup/verify-sms'
              : '/login/verify-sms',
          params: {
            anonymousId: anonymousId,
            emailAddress: emailAddress,
            phoneNumber: phoneNumber,
            session: sessionID,
            lastFourDigitPhoneNumber: lastFourDigitPhoneNumber,
            flowKey,
            ...notifyParams,
          },
        });
      }
      if (challenge === AuthChallengeEnum.verifyEmail.valueOf()) {
        router.replace({
          pathname:
            flowKey === OtpFlowKey.SIGNUP
              ? '/signup/verify-email'
              : '/login/verify-email',
          params: {
            anonymousId: anonymousId,
            emailAddress: emailAddress,
            phoneNumber: phoneNumber,
            session: sessionID,
            flowKey,
            ...notifyParams,
          },
        });
      }
      if (challenge === AuthChallengeEnum.verifyTokenLinkEmail.valueOf()) {
        router.replace({
          pathname: '/login/verify-identity',
          params: {
            anonymousId: anonymousId,
            session: sessionID,
            emailAddress: emailAddress,
            challengeId: challengeId,
            identityId: identityId,
            flowKey,
          },
        });
      }
      if (challenge === AuthChallengeEnum.resetPassword.valueOf()) {
        router.replace({
          pathname: '/reset-password/set-password',
          params: {
            anonymousId: anonymousId,
            emailAddress: emailAddress,
            phoneNumber: phoneNumber,
            session: sessionID,
            screenType: screenType,
            flowKey,
          },
        });
      }
      if (challenge === AuthChallengeEnum.verifyChangeEmail.valueOf()) {
        router.replace({
          pathname: '/myvaro/personal-details/change-email',
          params: {
            anonymousId: anonymousId,
            emailAddress: emailAddress,
            phoneNumber: phoneNumber,
            session: sessionID,
            screenType: screenType,
            flowKey,
          },
        });
      }
      if (challenge === AuthChallengeEnum.verifyChangePassword.valueOf()) {
        router.replace({
          pathname: '/myvaro/personal-details/change-password',
          params: {
            anonymousId: anonymousId,
            emailAddress: emailAddress,
            phoneNumber: phoneNumber,
            session: sessionID,
            screenType: screenType,
            flowKey,
          },
        });
      }
    },
    [router, segmentClient],
  );

  return { challengeRedirect };
};

export default useRedirect;
